/**
 * Admin Audit Oversight Page
 *
 * Shows all audits across the system for admin monitoring.
 * Admins can view audit progress, review reports, and track NCRs.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, CheckSquare, Clock, CheckCircle2, AlertTriangle, FileText, Eye } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { formatDate } from '../../utils/formatting';

interface Audit {
  id: string;
  auditId: string;
  applicationNumber: string;
  companyName: string;
  auditType: 'initial' | 'surveillance' | 'renewal';
  status: 'scheduled' | 'in_progress' | 'completed' | 'pending_review';
  auditor: string;
  scheduledDate?: string;
  completedDate?: string;
  progress: number;
  findings?: {
    conformities: number;
    minorNCs: number;
    majorNCs: number;
    observations: number;
  };
  ncrCount?: number;
}

export const AuditOversightPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setAudits([
        {
          id: 'audit-001',
          auditId: 'AUD-2025-001',
          applicationNumber: 'APP-2025-001',
          companyName: 'ABC Food Manufacturing Sdn Bhd',
          auditType: 'initial',
          status: 'completed',
          auditor: 'Sarah Ahmad',
          scheduledDate: '2025-12-15',
          completedDate: '2025-12-20',
          progress: 100,
          findings: {
            conformities: 42,
            minorNCs: 3,
            majorNCs: 0,
            observations: 5,
          },
          ncrCount: 3,
        },
        {
          id: 'audit-002',
          auditId: 'AUD-2025-002',
          applicationNumber: 'APP-2025-002',
          companyName: 'Premium Bakery Industries',
          auditType: 'surveillance',
          status: 'in_progress',
          auditor: 'Sarah Ahmad',
          scheduledDate: '2025-12-28',
          progress: 65,
          findings: {
            conformities: 28,
            minorNCs: 2,
            majorNCs: 0,
            observations: 3,
          },
          ncrCount: 2,
        },
        {
          id: 'audit-003',
          auditId: 'AUD-2025-003',
          applicationNumber: 'APP-2025-003',
          companyName: 'Global Ingredients Supply Co.',
          auditType: 'initial',
          status: 'scheduled',
          auditor: 'Mohammad Ali',
          scheduledDate: '2026-01-10',
          progress: 0,
        },
        {
          id: 'audit-004',
          auditId: 'AUD-2025-004',
          applicationNumber: 'APP-2025-004',
          companyName: 'Tasty Snacks Manufacturing',
          auditType: 'renewal',
          status: 'pending_review',
          auditor: 'Fatimah Hassan',
          scheduledDate: '2025-12-22',
          completedDate: '2025-12-27',
          progress: 100,
          findings: {
            conformities: 45,
            minorNCs: 1,
            majorNCs: 1,
            observations: 2,
          },
          ncrCount: 2,
        },
        {
          id: 'audit-005',
          auditId: 'AUD-2025-005',
          applicationNumber: 'APP-2025-005',
          companyName: 'Halal Catering Services',
          auditType: 'initial',
          status: 'in_progress',
          auditor: 'Ahmad Zaki',
          scheduledDate: '2025-12-29',
          progress: 35,
          findings: {
            conformities: 15,
            minorNCs: 1,
            majorNCs: 0,
            observations: 2,
          },
          ncrCount: 1,
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status: Audit['status']) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success" dot>Completed</Badge>;
      case 'in_progress':
        return <Badge variant="warning" dot>In Progress</Badge>;
      case 'scheduled':
        return <Badge variant="info" dot>Scheduled</Badge>;
      case 'pending_review':
        return <Badge variant="warning" dot>Pending Review</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: Audit['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-warning-600" />;
      case 'scheduled':
        return <FileText className="w-5 h-5 text-info-600" />;
      case 'pending_review':
        return <AlertTriangle className="w-5 h-5 text-warning-600" />;
    }
  };

  const getAuditTypeBadge = (type: Audit['auditType']) => {
    const labels = {
      initial: 'Initial Audit',
      surveillance: 'Surveillance',
      renewal: 'Renewal',
    };
    return <Badge variant="primary">{labels[type]}</Badge>;
  };

  const filteredAudits = audits.filter((audit) => {
    const matchesSearch = audit.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         audit.auditId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         audit.applicationNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter;
    const matchesType = typeFilter === 'all' || audit.auditType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <PageLayout
        pageTitle="Audit Oversight"
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: 'Audit Oversight' },
        ]}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="Audit Oversight"
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: 'Audit Oversight' },
      ]}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Audits</p>
                <p className="text-2xl font-bold text-gray-900">{audits.length}</p>
              </div>
              <CheckSquare className="w-8 h-8 text-primary-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-success-600">
                  {audits.filter(a => a.status === 'completed').length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-success-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-warning-600">
                  {audits.filter(a => a.status === 'in_progress').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total NCRs</p>
                <p className="text-2xl font-bold text-error-600">
                  {audits.reduce((sum, a) => sum + (a.ncrCount || 0), 0)}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-error-600" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by company name, audit ID, or application number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Statuses</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="pending_review">Pending Review</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Types</option>
                <option value="initial">Initial Audit</option>
                <option value="surveillance">Surveillance</option>
                <option value="renewal">Renewal</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Audit List */}
      {filteredAudits.length === 0 ? (
        <EmptyState
          icon={<CheckSquare className="w-12 h-12" />}
          title="No Audits Found"
          description="No audits match your current filters."
        />
      ) : (
        <div className="space-y-4">
          {filteredAudits.map((audit) => (
            <Card
              key={audit.id}
              hover
              className="cursor-pointer transition-shadow"
              onClick={() => navigate(`/admin/audits/${audit.id}`)}
            >
              <CardBody>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Status Icon */}
                    <div className="mt-1">
                      {getStatusIcon(audit.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {audit.auditId}
                        </h3>
                        {getStatusBadge(audit.status)}
                        {getAuditTypeBadge(audit.auditType)}
                        {audit.ncrCount && audit.ncrCount > 0 && (
                          <Badge variant="error">{audit.ncrCount} NCR{audit.ncrCount > 1 ? 's' : ''}</Badge>
                        )}
                      </div>

                      <p className="text-base text-gray-900 mb-2 font-medium">{audit.companyName}</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">Application:</span>{' '}
                          {audit.applicationNumber}
                        </div>
                        <div>
                          <span className="font-medium">Auditor:</span>{' '}
                          {audit.auditor}
                        </div>
                        <div>
                          <span className="font-medium">Scheduled:</span>{' '}
                          {audit.scheduledDate ? formatDate(audit.scheduledDate) : '-'}
                        </div>
                        {audit.completedDate && (
                          <div>
                            <span className="font-medium">Completed:</span>{' '}
                            {formatDate(audit.completedDate)}
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-medium text-gray-900">{audit.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              audit.progress === 100
                                ? 'bg-success-600'
                                : audit.progress > 0
                                ? 'bg-warning-600'
                                : 'bg-gray-400'
                            }`}
                            style={{ width: `${audit.progress}%` }}
                          />
                        </div>
                      </div>

                      {/* Findings Summary */}
                      {audit.findings && (
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">Findings:</span>
                          <span className="text-success-600 font-medium">
                            ✓ {audit.findings.conformities}
                          </span>
                          <span className="text-warning-600 font-medium">
                            ⚠ {audit.findings.minorNCs}
                          </span>
                          <span className="text-error-600 font-medium">
                            ✗ {audit.findings.majorNCs}
                          </span>
                          <span className="text-gray-500">
                            ℹ {audit.findings.observations}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Eye className="w-4 h-4" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/audits/${audit.id}`);
                    }}
                  >
                    View Details
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </PageLayout>
  );
};
